import { z } from 'zod';

import type { ListRequestQueryParams, PaginationQueryParams } from '@models';

/**
 * Stringy booleans to/from actual booleans
 */
export const stringToBoolean = z.codec(
  z.literal(['true', 'false']),
  z.boolean(),
  {
    decode: input => ({ true: true, false: false })[input],
    encode: output => output.toString() as 'true' | 'false',
  },
);

/**
 * Stringy floats to/from actual numbers. Non-finite input fails the output
 * schema, so a failed decode reads as no value.
 */
export const stringToNumber = z.codec(
  z.string(),
  z.number().refine(Number.isFinite),
  {
    // Number('') and Number('  ') are 0, so an empty input must fail the decode
    // rather than resolve to a real 0.
    decode: input => (input.trim() === '' ? Number.NaN : Number(input)),
    encode: output => String(output),
  },
);

/**
 * Stringy integers to/from actual numbers, discarding any fractional part.
 */
export const stringToInt = z.codec(
  z.string(),
  z.number().int().refine(Number.isFinite),
  {
    decode: input => Number.parseInt(input, 10),
    encode: output => String(output),
  },
);

/**
 * Wraps a possible string-ish value into an array. Useful for Express query
 * objects that only provide arrays when there are multiple values, but some
 * onward API request expects a strictly-typed array.
 */
export const coerceToArrayItems = <T extends z.ZodType = z.ZodString>(
  element?: T,
) =>
  z.preprocess(
    (value: string[] | string) => {
      if (typeof value === 'string') {
        return value.split(',').map(id => id.trim());
      }

      return value;
    },
    z.array<T>((element as T) ?? z.string()),
  );

/**
 * Wraps a possible string value into an array. Useful for Express query objects
 * that only provide arrays when there are multiple values, but some onward API
 * request expects an array.
 *
 * For string-ish values (e.g. enums) use {@link coerceToArrayItems} function.
 */
export const coerceToStringArray = coerceToArrayItems();

export const paginationQuerySchema = z.object({
  limit: z.coerce.number<string>().optional(),
  page: z.coerce.number<string>().optional(),
}) satisfies z.Schema<PaginationQueryParams>;

export const listQuerySchema = z.object({
  filter: z.string().optional(),
  sort: z.record(z.string(), stringToBoolean).optional(),
}) satisfies z.Schema<ListRequestQueryParams>;

export const sortablePaginationQuerySchema = z.object({
  ...paginationQuerySchema.shape,
  ...listQuerySchema.shape,
});

/**
 * Base query schema for cursor-based search endpoints. Each search service
 * extends this with its own filter params.
 */
export const searchPaginationQuerySchema = z.object({
  cursor: z.string().optional(),
  limit: z.coerce.number<string>().optional(),
  sort: z.record(z.string(), stringToBoolean).optional(),
});
